/**
 * PostHog wrapper — the only frontend module that speaks the posthog-js API.
 * While analytics is unconfigured or gated off, every exported function is a no-op.
 */
import {AnalyticsProp} from '@shared/analytics_events';
import type {AnalyticsEvent, IAnalyticsEventProps} from '@shared/analytics_events';
import type {IAuthUser} from '@shared/auth_types';
import {POSTHOG_CONFIG} from '@shared/config/posthog_config';
import posthog from 'posthog-js';

import {setTrpcExtraHeaders} from '@shared_ui/trpc_client';
import {__APP_ENV__} from '@src/app_env';

let analytics_enabled = false;

/**
 * Start PostHog when a token and host are configured; returns whether capture is on.
 * Development builds stay off unless VITE_POSTHOG_FORCE_DEV=true.
 */
export function initAnalytics(): boolean {
   const {token, host} = POSTHOG_CONFIG;

   if (token === '' || host === '') {
      console.warn('POSTHOG: no token/host configured — analytics disabled');
      return false;
   }

   const force_dev = import.meta.env.VITE_POSTHOG_FORCE_DEV === 'true';
   if (__APP_ENV__.DEVELOPMENT && !force_dev) {
      console.warn('POSTHOG: off in development (VITE_POSTHOG_FORCE_DEV=true enables it)');
      return false;
   }

   posthog.init(token, {
      api_host: host,
      ui_host: POSTHOG_CONFIG.uiHost,
      defaults: '2026-05-30',
   });
   posthog.register({
      [AnalyticsProp.APP]: 'main',
      [AnalyticsProp.ENVIRONMENT]: getEnvironmentName(),
      [AnalyticsProp.RELEASE]: __APP_ENV__.PACKAGE_VERSION,
   });

   analytics_enabled = true;
   setTrpcExtraHeaders(getAnalyticsRequestHeaders);
   return true;
}

/** Whether PostHog capture is on for this session. */
export function isAnalyticsEnabled(): boolean {
   return analytics_enabled;
}

/** Capture one catalog event. Standard properties are already registered; pass only the event's own. */
export function captureEvent<E extends AnalyticsEvent>(
   event: E,
   ...props: Record<string, never> extends IAnalyticsEventProps[E]
      ? [] | [IAnalyticsEventProps[E]]
      : [IAnalyticsEventProps[E]]
): void {
   if (!analytics_enabled) {
      return;
   }
   posthog.capture(event, props[0]);
}

/** Tie the session to the signed-in user by their Better Auth id. Safe to repeat. */
export function identifyUser(user: IAuthUser): void {
   if (!analytics_enabled) {
      return;
   }
   posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role,
   });
}

/** Drop the identified user and start a fresh anonymous session. Call on sign-out. */
export function resetIdentity(): void {
   if (!analytics_enabled) {
      return;
   }
   posthog.reset();
}

/** Stamp the active organization on all subsequent events; null clears it. */
export function setAnalyticsOrganization(orgId: string | null): void {
   if (!analytics_enabled) {
      return;
   }
   if (orgId == null) {
      posthog.unregister(AnalyticsProp.ORG_ID);
   } else {
      posthog.register({[AnalyticsProp.ORG_ID]: orgId});
   }
}

/** Report an unhandled error to PostHog error tracking. */
export function captureAnalyticsException(err: unknown): void {
   if (!analytics_enabled) {
      return;
   }
   posthog.captureException(err);
}

/**
 * Headers linking a backend request to this PostHog session and replay; empty when
 * analytics is off. Sent on every tRPC and REST call.
 */
export function getAnalyticsRequestHeaders(): Record<string, string> {
   if (!analytics_enabled) {
      return {};
   }
   return {
      'x-posthog-session-id': posthog.get_session_id(),
      'x-posthog-distinct-id': posthog.get_distinct_id(),
   };
}

function getEnvironmentName(): string {
   if (__APP_ENV__.PRODUCTION) {
      return 'production';
   }
   return __APP_ENV__.STAGING ? 'staging' : 'development';
}
